// GesellschaftPicker — Auswahl einer Gesellschaft aus den globalen
// GDV-Stammdaten (~460 Einträge) beim Anlegen eines Produktpartners.
//
// Bewusst kein <select>: bei 460 Optionen ist Tippen die einzige brauchbare
// Bedienung. Die Auswahl liefert den VOLLEN Datensatz zurück, damit die Maske
// Anschrift, Telefon, E-Mail und Website übernehmen kann — die Listen-
// Projektion allein hätte die Felder nicht.

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { useFormPrimitiveStyles } from "../forms/formPrimitives";
import {
  getGesellschaft,
  listGesellschaften,
  type GesellschaftDetail,
  type GesellschaftListItem,
} from "../../services/produktpartnerService";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

const useStyles = makeStyles({
  wrap: { position: "relative" },
  list: {
    position: "absolute",
    zIndex: 30,
    top: "100%",
    left: 0,
    right: 0,
    marginTop: "4px",
    maxHeight: "260px",
    overflowY: "auto",
    backgroundColor: "#fff",
    ...shorthands.border("1px", "solid", "#e2d8de"),
    ...shorthands.borderRadius("8px"),
    boxShadow: "0 8px 24px rgba(60,20,45,.14)",
  },
  item: {
    display: "block",
    width: "100%",
    textAlign: "left",
    ...shorthands.padding("8px", "12px"),
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    cursor: "pointer",
    fontSize: "13px",
    ":hover": { backgroundColor: "#f7f1f5" },
  },
  itemActive: { backgroundColor: "#f7f1f5" },
  itemSub: { display: "block", color: "#8a7683", fontSize: "12px" },
  empty: { ...shorthands.padding("10px", "12px"), color: "#8a7683", fontSize: "13px" },
  chosen: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
    ...shorthands.padding("8px", "12px"),
    ...shorthands.border("1px", "solid", "#e2d8de"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "#faf7f9",
    fontSize: "13px",
  },
  clearBtn: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: "#8a2f6b",
    cursor: "pointer",
    fontSize: "12px",
    textDecorationLine: "underline",
  },
});

export type GesellschaftPickerProps = {
  /** Aktuell gewählte Gesellschaft-Id ("" = keine). */
  value: string;
  /** Auswahl (oder Abwahl mit `null`). Der Volldatensatz trägt die Vorbefüllung. */
  onSelect: (g: GesellschaftDetail | null) => void;
  label?: string;
  hint?: string;
};

export const GesellschaftPicker: React.FC<GesellschaftPickerProps> = ({
  value,
  onSelect,
  label = "Gesellschaft (GDV-Stammdaten)",
  hint = "Übernimmt Anschrift, Telefon, E-Mail und Webseite aus dem Gesellschaftsverzeichnis.",
}) => {
  const s = useStyles();
  const fs = useFormPrimitiveStyles();

  const [alle, setAlle] = React.useState<GesellschaftListItem[]>([]);
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const ctrl = new AbortController();
    listGesellschaften(ctrl.signal)
      .then(setAlle)
      .catch(() => {
        /* ohne Liste bleibt das Feld leer — die Anlage funktioniert trotzdem */
      });
    return () => ctrl.abort();
  }, []);

  // Außenklick schließt die Vorschlagsliste.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    alleDokumente.addEventListener("pointerdown", onDown);
    return () => alleDokumente.removeEventListener("pointerdown", onDown);
  }, [open]);

  const gewaehlt = React.useMemo(
    () => (value ? alle.find((g) => String(g.id) === value) ?? null : null),
    [alle, value],
  );

  const treffer = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return alle.slice(0, 40);
    return alle.filter((g) => g.name.toLowerCase().includes(q)).slice(0, 40);
  }, [alle, query]);

  const waehle = async (g: GesellschaftListItem) => {
    setOpen(false);
    setQuery("");
    setBusy(true);
    // Details nachladen; scheitert das, reicht die Listen-Projektion für die
    // Verknüpfung — nur die Vorbefüllung entfällt dann.
    const detail = await getGesellschaft(g.id);
    setBusy(false);
    onSelect(detail ?? { ...g });
  };

  if (gewaehlt || (value && busy)) {
    return (
      <div className={fs.field}>
        <label className={fs.label}>{label}</label>
        <div className={s.chosen}>
          <span>
            {gewaehlt?.name ?? "wird geladen…"}
            {gewaehlt?.ort ? ` · ${gewaehlt.ort}` : ""}
          </span>
          <button type="button" className={s.clearBtn} onClick={() => onSelect(null)}>
            Ändern
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={fs.field}>
      <label className={fs.label} htmlFor="gesellschaft-suche">
        {label}
      </label>
      <div className={s.wrap} ref={wrapRef}>
        <input
          id="gesellschaft-suche"
          name="gesellschaft-suche"
          autoComplete="off"
          className={fs.input}
          value={query}
          placeholder="Name der Gesellschaft eingeben, z. B. Generali"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
        {open && (
          <div className={s.list} role="listbox">
            {treffer.length === 0 ? (
              <div className={s.empty}>Keine Gesellschaft gefunden.</div>
            ) : (
              treffer.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className={mergeClasses(s.item, String(g.id) === value && s.itemActive)}
                  onClick={() => void waehle(g)}
                >
                  {g.name}
                  {g.ort && <span className={s.itemSub}>{g.ort}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {hint && <div className={fs.hintMsg}>{hint}</div>}
    </div>
  );
};

export default GesellschaftPicker;
